import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Tables } from './database.types';
import { useSupabase } from './provider';

export type Profile = Tables<'profiles'>;

const profileKey = (userId: string | undefined) => ['profile', userId] as const;

export function useMyProfile(userId: string | undefined) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: profileKey(userId),
    enabled: Boolean(userId),
    queryFn: async (): Promise<Profile | null> => {
      if (!userId) return null;
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useUpdateProfile(userId: string | undefined) {
  const supabase = useSupabase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { displayName: string }): Promise<void> => {
      if (!userId) throw new Error('Not signed in');
      const { error } = await supabase
        .from('profiles')
        .update({ display_name: input.displayName.trim() })
        .eq('id', userId);
      if (error) throw error;
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: profileKey(userId) }),
        // Member lists show display names.
        qc.invalidateQueries({ queryKey: ['groups'] }),
      ]),
  });
}
